/**
 * Layout constants and class strings shared by every `LedgerTable` row kind.
 *
 * Pure move out of `LedgerTable.tsx` (review F1) — no behaviour change: the
 * header, skeleton, group-header and data rows all need the same frozen-column
 * geometry, and `LedgerRow.tsx` is now its own module, so the strings can no
 * longer live inside the host component's file. Every comment below is the
 * original one, verbatim.
 */

export const ROW_NUMBER_WIDTH = 40;
/** 16 gutter + 16 control + 16 gutter — must equal `2 × --gutter + control`
 *  (`LedgerTable.tsx` sets `[--gutter:16px]`); was 36 and clipped the
 *  checkbox against the gutters (Spencer QA). */
export const CHECKBOX_COLUMN_WIDTH = 48;
/** `rowLead` column (the Notes bubble: icon + count) — 44px of content inside `ROW_LEAD_CELL_CLASS`'s 6px gutters. */
export const ROW_LEAD_WIDTH = 56;
/**
 * The `rowLead` cell's own class, on every row kind:
 * - `!px-1.5` — tight 6px gutters so the bubble fits the narrow column. The `!` is required: Glaze's
 *   cell base (`px-2 first:pl-(--gutter)`) wins otherwise, because `first:` out-specifies a plain
 *   `px-*` and the lead is often the row's first cell. The table layer's only `!important`.
 * - `[&_[data-row-link]]:pointer-events-none` — on an href row Glaze puts its row link in every cell
 *   as an `absolute inset-0` overlay, so a click in the lead's gutter lands on that link and
 *   navigates. The cell's `stopPropagation` (or a `preventDefault`) runs too late to stop a link
 *   activation that is already the click target; only taking the overlay out of hit-testing here
 *   works. The bubble itself sits above it (`relative`), and every other cell keeps the link.
 */
export const ROW_LEAD_CELL_CLASS = '!px-1.5 [&_[data-row-link]]:pointer-events-none';
export const TRACKER_COLUMN_WIDTH = 120;
export const DEFAULT_COLUMN_WIDTH = 140;
export const NAME_COLUMN_WIDTH = 188;
/** Width hb reserves for a trailing row-action column in the table's min-width sum. */
export const ROW_ACTION_WIDTH = 96;
/** Sub-pixel headroom added to a measured `rowActionWidth: 'auto'` column —
 *  `scrollWidth` is integer-rounded and can read up to 1px short. */
export const AUTO_ACTION_SLACK_PX = 2;
/** cdx3 #2 — the least scroll-corridor width the unpinned (middle) columns
 *  must keep between a pinned identity and a pinned Actions column; below it
 *  Actions stops pinning so the middle columns stay reachable. */
export const MIN_SCROLLABLE_DATA_PX = 240;
export const STICKY_SURFACE = 'bg-bg-primary';

/**
 * Right hairline on a frozen column's trailing edge, drawn as a pseudo-element
 * so it stays put under `sticky` (hb `GroupedTable.tsx` STICKY_RIGHT_HAIRLINE).
 */
export const STICKY_RIGHT_HAIRLINE =
  "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-border-primary after:content-['']";

/**
 * Left hairline on a right-frozen column's leading edge — the mirror of
 * `STICKY_RIGHT_HAIRLINE`, drawn `before:` so it never collides with the
 * Name cell's `before:` (`SELECTED_ACCENT_BAR`, a different cell).
 */
export const STICKY_LEFT_HAIRLINE =
  "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-border-primary before:content-['']";

/**
 * Table-level classes for the vertical cell hairlines, so a layout can opt out
 * (`FlowTableConfig.hideColumnDividers`). The grid keeps the per-cell
 * `border-r` between ordinary cells; the opt-out drops only those. The frozen
 * strips' pseudo-element edges (`after:` on the first sticky column, `before:`
 * on the Actions column) are not part of the opt-out and always draw.
 */
export const COLUMN_DIVIDER_CLASS =
  '[&_th:not(:last-child)]:border-r-[0.5px] [&_td:not(:last-child):not([data-sticky])]:border-r-[0.5px]';
export const NO_COLUMN_DIVIDER_CLASS = '';

/**
 * Frozen-column classes shared by the Name cell of EVERY body row (skeleton,
 * group header, data) so horizontal scroll freezes one identical strip. The
 * row's surface (background) is added per row kind; the `<th>` uses the same
 * geometry at `z-20`.
 */
export const FROZEN_NAME_CELL = `sticky left-0 z-[1] !pl-3.5 ${STICKY_RIGHT_HAIRLINE}`;

/**
 * Frozen-column classes shared by the Actions cell of EVERY row kind,
 * mirroring `FROZEN_NAME_CELL` on the trailing edge — one shared builder; the
 * row's opaque surface is layered on top per row kind exactly like Name. Body
 * cells (skeleton, group header, data) sit at `z-[1]`; the `<th>` uses the
 * same geometry at `z-20`, matching the Name header's `identityHeaderClass`.
 */
export const frozenActionCell = (zClass: string) => `sticky right-0 ${zClass} ${STICKY_LEFT_HAIRLINE}`;
/** Body-row Actions strip (`z-[1]`) — see `frozenActionCell`. */
export const FROZEN_ACTION_CELL = frozenActionCell('z-[1]');
/** Header `<th>` Actions strip (`z-20`) — see `frozenActionCell`. */
export const FROZEN_ACTION_HEADER_CELL = frozenActionCell('z-20');

/**
 * 2px left accent bar on a selected row's Name cell. A pseudo-element on the
 * `<tr>` itself would render as an anonymous cell in table layout and shift
 * every column one slot right, so the bar lives on the frozen Name cell.
 */
export const SELECTED_ACCENT_BAR =
  "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-border-selected before:content-['']";

/**
 * Every cell of an `href` row gets Glaze's react-router `Link` overlay as its
 * FIRST child (`table.tsx:372-381`: `absolute inset-0`, no z-index). Static
 * content after it paints — and hit-tests — underneath, so a click on a
 * Domain/LinkedIn/Meeting anchor landed on the overlay and opened the account
 * instead (Codex r2 #1). A positioned descendant later in DOM order paints
 * above a non-z-indexed positioned sibling, so every interactive element in
 * the cell is made `relative`; plain text stays under the overlay, which is
 * what keeps the row navigable from non-interactive areas. Same mechanism
 * as the tracker/rowAction cells' `relative inline-flex` wrappers, applied
 * generically so a renderer never has to know about the overlay. Glaze's own
 * overlay is excluded via its `data-row-link` marker so it keeps `absolute`.
 */
export const HREF_ROW_INTERACTIVE_CLASS =
  '[&_a:not([data-row-link])]:relative [&_button]:relative [&_input]:relative [&_select]:relative [&_[role=button]]:relative [&_[role=link]]:relative';

/**
 * Bounded-mode corridor cap when a caller passes no `maxHeightClassName` —
 * V1 parity with `FlowTable.tsx`'s own `maxHeightClassName ?? 'max-h-[calc(100dvh-8rem)]'`.
 */
export const DEFAULT_BOUNDED_MAX_HEIGHT_CLS = 'max-h-[calc(100dvh-8rem)]';

/**
 * The `fillHeight` contract, in ONE place (review F4 + its nit twin).
 *
 * `FlowTableProps.fillHeight` (`types.ts`) is documented as two-state with
 * `false` as the default — "the card caps at `max-h-[calc(100dvh-8rem)]`" —
 * and V1 `FlowTable.tsx:520` implements exactly that (`fillHeight = false`).
 * `LedgerTable` used to read `fillHeight === false && !!(max || min)`, which
 * made an omitted `fillHeight` FILL and made a bare `fillHeight={false}` fill
 * too: a third state the shared type does not describe. Both the ledger and
 * `PeopleTable` (which caps the row holding the pane AND the table) ask here,
 * so the two can't drift.
 */
export function isBoundedHeight(fillHeight: boolean | undefined): boolean {
  return fillHeight !== true;
}

/** Floor for a drag-resize — keeps the handle grabbable (FlowTable parity). */
export const MIN_COLUMN_WIDTH_PX = 60;

// ── Row virtualization (plan 2026-09-29-002) ───────────────────────────────
// Only the V2 ledger windows its rows; V1 `FlowTable` is untouched. These are
// first-paint ESTIMATES only — every mounted row re-measures itself through
// react-virtual's `measureElement` (D1), so a wrong estimate costs a window
// recalculation, never a layout error.

/** A Glaze dense data row / group-header row: `py-2` + `text-sm/6` + a 0.5px
 *  hairline ≈ 40px. Both kinds share it — they render the same cell geometry. */
export const LEDGER_ROW_ESTIMATE_PX = 40;
/** A Growth Strategist Companies row: the Account cell's name over its
 *  "Segment · Industry" line runs two lines. 56 is the estimate of
 *  the GS design (egs-view at e7603325b). */
export const GS_LEDGER_ROW_ESTIMATE_PX = 56;
/** A Signal Response row: two-line cells (36px of text) in `py-3` cell padding
 *  (12px × 2) plus the hairline ≈ 60px. Estimate only; rows self-measure. */
export const ROOMY_LEDGER_ROW_ESTIMATE_PX = 60;
/** The `group-gap` row before every level-0 header after the first — the
 *  measured twin of its `h-5` class (Tailwind can't take a JS constant, so
 *  the two are kept adjacent here deliberately). */
export const LEDGER_GAP_ROW_PX = 20;
/** The expanded detail band (`ContactDetail` / `SignalDetail`) runs 300–900px
 *  in practice; 320 is the low end, so a first paint under-reaches rather than
 *  over-reaches and the real measurement corrects it in the same commit. */
export const LEDGER_DETAIL_ESTIMATE_PX = 320;
/** Items mounted beyond each viewport edge. 8 ≈ 320px of runway — enough that
 *  a row-anchored popover's own `isAnchorOutOfView` close (a capture-phase
 *  window scroll listener) always fires before windowing can unmount its row. */
export const LEDGER_OVERSCAN = 8;
/** Corridor height assumed when `clientHeight` reads 0 — jsdom (no layout),
 *  `display:none`, and the first render before the scroll element exists.
 *  react-virtual renders NOTHING for a 0-height viewport (`calculateRange`
 *  returns `null`), so without this every existing V2 test — and the first
 *  paint of the real app — would show an empty table. */
export const LEDGER_VIEWPORT_FALLBACK_PX = 800;

// Group header surface per depth. Nesting is conveyed by indent + surface;
// per-column gridlines run through every group header row.
export const DEPTH_BG = ['bg-bg-secondary', 'bg-bg-secondary', ''];

/** Top hairline on a level-0 group header — gives each top-level block a
 *  visible top edge (the block's bottom edge is just the ordinary `border-b`
 *  every `<td>` already carries). */
export const LEVEL_ZERO_TOP_HAIRLINE = 'border-t-[0.5px] border-border-primary';

/**
 * "Active" skin for a toolbar trigger — Glaze's OWN pressed-state tokens
 * (`ToggleButton`'s `data-pressed:` set, hover included), applied via the
 * element's own `data-active` attribute rather than a bare conditional class:
 * Glaze merges `className` with `clsx` (not `twMerge`), so a bare
 * `bg-bg-selected` next to a variant's `bg-bg-primary` only won by Tailwind's
 * alphabetical output order. A `[data-active]` selector outranks the bare
 * variant utilities outright. Originally `LedgerToolbar`'s own
 * `TRIGGER_ACTIVE_CLASS` (Group/Sort triggers); moved here so the Plays
 * toolbar's "Back to all people" / "Showing all people" control (fix-plan
 * item 2) can share the identical recipe.
 */
export const TRIGGER_ACTIVE_CLASS = [
  'data-active:border-border-selected data-active:bg-bg-selected data-active:text-content-selected',
  'data-active:hover:border-border-selected-hover data-active:hover:bg-bg-selected-hover',
].join(' ');

/**
 * The ledger column (toolbar + table): the NAMED `ledger` container that the toolbar's narrow mode
 * (`@max-[64rem]/ledger:`) and the selection cluster's wrapped second line key off. Tailwind scans for whole class tokens, so the narrow variants are written out
 * literally where they are used; this is the one place the container itself is named.
 */
export const LEDGER_COLUMN_CLASS = '@container/ledger';

/**
 * The WRAPPING toolbar row (Signals): at least 48px, `flex-wrap` so a narrow Flow column grows a
 * second line instead of clipping controls off the right edge (the fixed row below scrolls horizontally, which hides
 * the Columns button and the far-right controls at half-screen width). The row is NOT a container itself (see `LEDGER_COLUMN_CLASS`): the ledger column's width, not the viewport's, decides.
 */
// Narrow: the right group can be two lines tall (icons + the wrapped selection cluster). `items-center` would then
// centre the left controls against BOTH lines and push them below the icons, so narrow top-aligns the row instead;
// `py-2` + the 32px controls reproduce the single-line 48px centring, so both groups share row 1's line.
export const TOOLBAR_ROW_WRAP_CLASS =
  'flex min-h-12 shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b-[0.5px] border-border-primary bg-bg-primary px-4 py-1 @max-[64rem]/ledger:items-start @max-[64rem]/ledger:py-2 @min-[64rem]/ledger:@max-[80rem]/ledger:gap-y-2';

/** The right cluster inside the wrapping row: it may wrap internally and keeps its content right-aligned. */
// MID band (64rem to 80rem of the ledger column: text buttons still shown, but the right group no longer fits beside the
// left controls and drops to row 2): it left-aligns under row 1 instead of hugging the right edge, with an 8px row gap
// (`gap-y-2` on the row). Below 64rem (icon-only) and above 80rem (one row) it is unchanged.
export const TOOLBAR_RIGHT_WRAP_CLASS =
  'ml-auto flex flex-wrap items-center justify-end gap-x-2 gap-y-1 @min-[64rem]/ledger:@max-[80rem]/ledger:ml-0 @min-[64rem]/ledger:@max-[80rem]/ledger:justify-start';

/** The 48px toolbar row shared by the ledger toolbar and Home's toolbar. */
export const TOOLBAR_ROW_CLASS =
  'flex h-12 shrink-0 items-center gap-2 overflow-x-auto border-b-[0.5px] border-border-primary bg-bg-primary px-4';
